import { Center, Stack, VStack } from "@chakra-ui/react";
import useAuth from "../../hooks/useAuth";
import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ActivateAccountRequestDto,
  ResetPasswordRequestDto,
} from "../../services/Dtos";
import {
  validatePassword,
  validateRequired,
  validateConfirmPassword,
} from "../../services/validation";
import useToaster from "../../hooks/useToaster";
import FormContainer from "../CustomUi/FormContainer";
import PasswordInput from "../CustomUi/CustomPasswordInput";
import AuthPagesHeader from "../CustomUi/AuthPagesHeader";
import CustomButton from "../CustomUi/CustomButton";
import FormNavigationLinks from "../CustomUi/FormNavigationLinks";
import { useFormChanges } from "../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../hooks/useFormFieldHandler";
import CustomAlert from "../CustomUi/CustomAlert";
import useIcons from "../../hooks/useIcons";
import useThemeColors from "../../hooks/useThemeColors";

export default function ActivateAccountForm() {
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const icons = useIcons();
  const { activateAccount, loading, error } = useAuth();
  const [resetPasswordSuccess, setResetPasswordSuccess] =
    useState<boolean>(false);
  const query = useQuery();
  const navigate = useNavigate();
  const [resetPasswordData, setResetPasswordData] =
    useState<ActivateAccountRequestDto>({
      newpassword: "",
      confirmPassword: "",
      email: "",
      token: "",
    });
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const colors = useThemeColors();
  const { showSuccessToast, showErrorToast } = useToaster();

  // Define field configurations for form validation
  const fieldConfigs = {
    newpassword: { type: "text" as const, required: true },
    confirmPassword: { type: "text" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: null,
      currentData: resetPasswordData,
      isEditMode: false,
    });

  // Use the form field handler hook
  const { handleFieldChange, handleFieldBlur } = useFormFieldHandler({
    setFormState: setResetPasswordData,
    setTouchedFields,
  });

  // Custom validation for password
  const validatePasswordField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password is required";
    }
    if (!validatePassword(value)) {
      return "Password must have 8 characters including 1 number, 1 uppercase letter, 1 lowercase letter and 1 special character";
    }
    return "";
  };

  // Custom validation for password confirmation
  const validatePasswordConfirmation = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password confirmation is required";
    }
    if (!validateConfirmPassword(value, resetPasswordData.newpassword)) {
      return "Passwords do not match";
    }
    return "";
  };

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof ResetPasswordRequestDto;

    // Custom validation for specific fields
    if (name === "newpassword") {
      return validatePasswordField(value);
    }
    if (name === "confirmPassword") {
      return validatePasswordConfirmation(value);
    }

    return validateField(fieldKey, value);
  };

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      resetPasswordData[fieldName as keyof ActivateAccountRequestDto]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      resetPasswordData[fieldName as keyof ActivateAccountRequestDto]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();

    // Check if form has changes
    if (!hasChanges) {
      return;
    }

    // Validate all fields
    const errors = validateAllFields();
    if (hasValidationErrors(errors)) {
      return;
    }

    const email = query.get("email");
    let token = query.get("token");
    if (!token || !email) {
      showErrorToast("Invalid Account Activation Link");
      return;
    }
    try {
      token = token.replace(/ /g, "+");
      const payload = {
        ...resetPasswordData,
        email,
        token,
      };
      const result = await activateAccount(payload);
      if (result?.succeeded) {
        setResetPasswordSuccess(true);
        showSuccessToast("Account activated successfully!");
      } else {
        showErrorToast(
          result?.message || "Failed to activate account. Please try again."
        );
      }
    } catch (error) {
      showErrorToast("An unexpected error occurred.");
    }
  };

  return (
    <Center h={"full"} w={"full"}>
      <FormContainer
        heading={
          resetPasswordSuccess ? "Account Activated!" : "Activate Your Account"
        }
        description={
          resetPasswordSuccess
            ? ""
            : "Please enter a new password below to activate your account."
        }
      >
        {resetPasswordSuccess ? (
          <>
            <AuthPagesHeader
              heading={""}
              description={
                "Your account has been activated successfully. You can now log in with your new password."
              }
            />
            <FormNavigationLinks
              links={[
                {
                  text: "Go to Login",
                  icon: icons.utility.login,
                  onClick: () => navigate("/auth/login"),
                },
              ]}
            />
          </>
        ) : (
          <>
            {error.length > 0 && (
              <VStack gap={2} mb={4} align={"stretch"}>
                {error.slice(0, 3).map((errorMessage, index) => (
                  <CustomAlert key={index} message={errorMessage} tight />
                ))}
              </VStack>
            )}
            <form onSubmit={handleChangePassword} style={{ width: "100%" }}>
              <Stack gap={4}>
                <PasswordInput
                  placeholder="Password *"
                  name="newpassword"
                  value={resetPasswordData.newpassword}
                  onChange={handleFieldChange}
                  onBlur={() => handleFieldBlur("newpassword")}
                  invalid={showFieldError("newpassword")}
                  error={
                    showFieldError("newpassword")
                      ? getFieldError("newpassword")
                      : ""
                  }
                  required
                />

                <PasswordInput
                  placeholder="Confirm Password *"
                  name="confirmPassword"
                  value={resetPasswordData.confirmPassword}
                  onChange={handleFieldChange}
                  onBlur={() => handleFieldBlur("confirmPassword")}
                  invalid={showFieldError("confirmPassword")}
                  error={
                    showFieldError("confirmPassword")
                      ? getFieldError("confirmPassword")
                      : ""
                  }
                  required
                />

                <CustomButton
                  label="Activate Account"
                  type="submit"
                  w={"full"}
                  loading={loading}
                  disabled={loading || !hasChanges || hasValidationErrorsValue}
                  colorPalette={colors.brand.primary}
                />
              </Stack>
            </form>
          </>
        )}
      </FormContainer>
    </Center>
  );
}
